import { PlannerTask, ScheduledOccurrence, DashboardDailyMetrics, OccurrenceStatus, WeeklyRoutineTask } from '../types';
import { apiGet, apiPost, apiPut, apiDelete } from './api';
import { calculateDailyMetrics } from './scoringEngine';

type PlannerChangeListener = () => void;
const listeners: PlannerChangeListener[] = [];

let initialSyncCompleted = false;

export function isInitialPlannerSyncComplete() {
  return initialSyncCompleted;
}

export function subscribeToPlannerChanges(listener: PlannerChangeListener) {
  listeners.push(listener);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

export function notifyPlannerChanged() {
  initialSyncCompleted = true;
  // Invalidate in-memory occurrence cache to force re-fetch from single source of truth (Cloudflare D1)
  Object.keys(occurrencesCache).forEach(k => delete occurrencesCache[k]);
  listeners.forEach(fn => {
    try { fn(); } catch (e) { console.warn(e); }
  });
}

// In-memory cache for fast local optimistic rendering
const occurrencesCache: Record<string, DashboardDailyMetrics> = {};

/**
 * Convert PlannerTask from D1 database into WeeklyRoutineTask format for UI components
 */
export function mapPlannerTaskToWeeklyRoutineTask(task: PlannerTask): WeeklyRoutineTask {
  const dayOfWeek = Array.isArray(task.daysOfWeek) && task.daysOfWeek.length > 0 ? Number(task.daysOfWeek[0]) : 0;
  return {
    id: task.id,
    dayOfWeek: isNaN(dayOfWeek) ? 0 : dayOfWeek,
    time: task.startTime || '08:00',
    endTime: task.endTime || undefined,
    title: task.title,
    description: task.description || undefined,
    category: (task.category as any) || 'work',
    reminderEnabled: true,
    phoneNotificationEnabled: true,
    wentToWorkChecked: task.category === 'work',
    attendedSfiChecked: task.category === 'sfi' || task.category === 'swedish',
    programId: task.programId || undefined,
    programDayId: task.programDayId || undefined,
    defaultScore: task.defaultScore,
    scoreMode: (task.scoreMode as any) || 'auto',
    customScore: task.customScore || undefined,
    isRequired: task.isRequired !== false
  };
}

/**
 * Convert WeeklyRoutineTask from UI components into PlannerTask format for D1 database
 */
export function mapWeeklyRoutineTaskToPlannerTask(task: Partial<WeeklyRoutineTask>): Partial<PlannerTask> {
  const days = typeof task.dayOfWeek === 'number' ? [task.dayOfWeek] : [1];
  return {
    id: task.id,
    title: task.title,
    description: task.description || undefined,
    category: task.category || 'work',
    recurrenceType: 'weekly',
    daysOfWeek: days,
    startTime: task.time || '08:00',
    endTime: task.endTime || undefined,
    defaultScore: task.defaultScore,
    scoreMode: task.scoreMode || 'auto',
    customScore: task.customScore || undefined,
    isRequired: task.isRequired !== false,
    isActive: true,
    programId: task.programId,
    programDayId: task.programDayId
  };
}

/**
 * Fetch occurrences and metrics for a specific date (YYYY-MM-DD).
 * Single Source of Truth from Cloudflare D1 database.
 */
export async function getDashboardMetricsForDate(dateStr: string, forceRefresh = false): Promise<DashboardDailyMetrics> {
  if (!forceRefresh && occurrencesCache[dateStr] && occurrencesCache[dateStr].occurrences && occurrencesCache[dateStr].occurrences.length > 0) {
    // Return cached immediately for fast UI response, trigger background sync from D1
    fetchOccurrencesFromApi(dateStr).then(metrics => {
      if (metrics) {
        occurrencesCache[dateStr] = metrics;
        notifyPlannerChanged();
      }
    }).catch(() => {});
    return occurrencesCache[dateStr];
  }

  const fresh = await fetchOccurrencesFromApi(dateStr);
  if (fresh) {
    occurrencesCache[dateStr] = fresh;
    return fresh;
  }

  // If network unavailable or error, return in-memory cache or empty state from D1 schema
  return occurrencesCache[dateStr] || calculateDailyMetrics([]);
}

async function fetchOccurrencesFromApi(dateStr: string): Promise<DashboardDailyMetrics | null> {
  try {
    const res = await apiGet<{ metrics: DashboardDailyMetrics; occurrences: ScheduledOccurrence[] }>(
      `/api/planner/occurrences?date=${encodeURIComponent(dateStr)}`
    );

    const metrics = (res as any).metrics || res.data?.metrics;
    if (res.success && metrics) {
      return metrics;
    }
  } catch (err) {
    console.warn("[PlannerClient] Error fetching occurrences from D1:", err);
  }
  return null;
}

/**
 * Fetch occurrences and metrics for a specific date range in a single batch request from Cloudflare D1.
 */
export async function getDashboardMetricsRange(
  startDate: string,
  endDate: string,
  forceRefresh = false
): Promise<Record<string, DashboardDailyMetrics>> {
  const result: Record<string, DashboardDailyMetrics> = {};

  try {
    const res = await apiGet<{
      metricsByDate: Record<string, DashboardDailyMetrics>;
      totalOccurrences: number;
    }>(
      `/api/planner/occurrences/range?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
    );

    const metricsByDate = (res as any).metricsByDate || res.data?.metricsByDate;
    if (res.success && metricsByDate) {
      Object.entries(metricsByDate).forEach(([dKey, metrics]) => {
        occurrencesCache[dKey] = metrics as DashboardDailyMetrics;
        result[dKey] = metrics as DashboardDailyMetrics;
      });
      return result;
    }
  } catch (err) {
    console.warn("[PlannerClient] Error fetching occurrences range from D1:", err);
  }

  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);

  while (cur <= end) {
    const yStr = cur.getFullYear();
    const mStr = String(cur.getMonth() + 1).padStart(2, '0');
    const dStr = String(cur.getDate()).padStart(2, '0');
    const dateKey = `${yStr}-${mStr}-${dStr}`;

    result[dateKey] = occurrencesCache[dateKey] || calculateDailyMetrics([]);
    cur.setDate(cur.getDate() + 1);
  }

  return result;
}

/**
 * Optimistically toggle an occurrence completion and sync with Cloudflare D1.
 */
export async function toggleOccurrenceStatus(
  dateStr: string,
  occurrenceId: string,
  newStatus?: OccurrenceStatus
): Promise<{ success: boolean; newStatus: OccurrenceStatus }> {
  // Optimistic UI update in local in-memory cache
  if (occurrencesCache[dateStr]) {
    const occs = occurrencesCache[dateStr].occurrences;
    const target = occs.find(o => o.id === occurrenceId || o.plannerTaskId === occurrenceId);
    if (target) {
      const nextStatus: OccurrenceStatus = newStatus || (target.status === 'completed' ? 'pending' : 'completed');
      target.status = nextStatus;
      target.completedAt = nextStatus === 'completed' ? new Date().toISOString() : null;
      occurrencesCache[dateStr] = calculateDailyMetrics([...occs]);
      notifyPlannerChanged();
    }
  }

  try {
    const res = await apiPut<{ status: OccurrenceStatus }>(
      `/api/planner/occurrences/${encodeURIComponent(occurrenceId)}/toggle`,
      { status: newStatus }
    );

    const status = (res as any).status || res.data?.status;
    if (res.success && status) {
      const updated = await fetchOccurrencesFromApi(dateStr);
      if (updated) {
        occurrencesCache[dateStr] = updated;
        notifyPlannerChanged();
      }
      return { success: true, newStatus: status };
    }
  } catch (err) {
    console.error("[PlannerClient] Error toggling occurrence status in D1:", err);
  }

  return { success: false, newStatus: 'pending' };
}

/**
 * Fetch all Planner Tasks for current user from Cloudflare D1 database
 */
export async function getPlannerTasks(): Promise<PlannerTask[]> {
  try {
    const res = await apiGet<PlannerTask[]>('/api/planner/tasks');
    if (res.success && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (err) {
    console.warn("[PlannerClient] Error fetching planner tasks from D1:", err);
  }
  return [];
}

/**
 * Create or update a Planner Task directly in Cloudflare D1 database
 */
export async function savePlannerTask(task: Partial<PlannerTask>): Promise<{ success: boolean; data?: PlannerTask; error?: string }> {
  try {
    let res;
    if (task.id) {
      res = await apiPut<PlannerTask>(`/api/planner/tasks/${encodeURIComponent(task.id)}`, task);
    } else {
      res = await apiPost<PlannerTask>('/api/planner/tasks', task);
    }

    if (res.success) {
      Object.keys(occurrencesCache).forEach(k => delete occurrencesCache[k]);
      notifyPlannerChanged();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error || "Failed to save planner task" };
  } catch (err: any) {
    return { success: false, error: err?.message || "Server communication error" };
  }
}

/**
 * Delete a Planner Task directly from Cloudflare D1 database
 */
export async function deletePlannerTask(taskId: string): Promise<boolean> {
  try {
    const res = await apiDelete(`/api/planner/tasks/${encodeURIComponent(taskId)}`);
    if (res.success) {
      Object.keys(occurrencesCache).forEach(k => delete occurrencesCache[k]);
      notifyPlannerChanged();
      return true;
    }
  } catch (err) {
    console.error("[PlannerClient] Error deleting planner task from D1:", err);
  }
  return false;
}

